import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import AboutFormat from './components/AboutFormat';
import BriefingSteps from './components/BriefingSteps';
import CtaSection from './components/CtaSection';
import PackageFeatures from './components/PackageFeatures';
import RecordingLocation from './components/RecordingLocation';
import Footer from './components/Footer';

const WHATSAPP_URL =
  'https://wa.me/5592984398338?text=Ol%C3%A1!%20Vim%20pelo%20site%20do%20Manaus%20Digital%20Podcast%20e%20gostaria%20de%20reservar%20o%20meu%20epis%C3%B3dio.%20Pode%20me%20ajudar%3F';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaData = [
  {
    '@context': 'https://schema.org',
    '@type': 'PodcastSeries',
    name: 'Manaus Digital Podcast',
    description:
      'Compartilhe sua história no principal podcast de empreendedorismo e inovação da Amazônia. Episódio profissional de 30-60 minutos com gravação, edição e divulgação completa. Hosts Léo David e Rafael Veiga.',
    url: SITE_URL,
    inLanguage: 'pt-BR',
    author: [
      { '@type': 'Person', name: 'Léo David' },
      { '@type': 'Person', name: 'Rafael Veiga' },
    ],
    image: 'https://static.readdy.ai/image/5da477fdae7b1d111ee386eccbf37db2/5e84d38391d78bdbfa9f5405de36d583.png',
    genre: ['Empreendedorismo', 'Inovação', 'Startups', 'Negócios'],
    locationCreated: {
      '@type': 'Place',
      name: 'Manaus',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Manaus',
        addressRegion: 'AM',
        addressCountry: 'BR',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Seja Entrevistado no Manaus Digital Podcas',
    description:
      'Compartilhe sua história no principal podcast de empreendedorismo e inovação da Amazônia. Episódio profissional de 30-60 minutos com gravação, edição e divulgação completa. Hosts Léo David e Rafael Veiga.',
    url: SITE_URL,
    image: 'https://static.readdy.ai/image/5da477fdae7b1d111ee386eccbf37db2/5e84d38391d78bdbfa9f5405de36d583.png',
    brand: { '@type': 'Brand', name: 'Manaus Digital Podcast' },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'BRL',
      price: '500.00',
      availability: 'https://schema.org/InStock',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Seja Entrevistado no Manaus Digital Podcas',
    description:
      'Compartilhe sua história no principal podcast de empreendedorismo e inovação da Amazônia. Episódio profissional de 30-60 minutos com gravação, edição e divulgação completa. Hosts Léo David e Rafael Veiga.',
    url: SITE_URL,
    inLanguage: 'pt-BR',
    dateModified: '2025-07-15',
    publisher: {
      '@type': 'Organization',
      name: 'Manaus Digital Podcast',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/5da477fdae7b1d111ee386eccbf37db2/5e84d38391d78bdbfa9f5405de36d583.png',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Quanto custa gravar um episódio no Manaus Digital Podcast?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O episódio profissional custa R$ 500 e inclui gravação, edição e divulgação completa.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual a duração do episódio no Manaus Digital Podcast?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Os episódios têm duração de 30 a 60 minutos, com formato de entrevista profissional.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quem são os hosts do Manaus Digital Podcast?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Os hosts são Léo David e Rafael Veiga, especialistas em empreendedorismo e inovação na Amazônia.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como faço para reservar meu episódio no Manaus Digital Podcast?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Entre em contato pelo WhatsApp (92) 98439-8338 ou pelo Instagram @manausdigital.br para reservar seu episódio.',
        },
      },
    ],
  },
];

export default function Home() {
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[data-schema="home"]');
    existingScripts.forEach((s) => s.remove());

    schemaData.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'home');
      script.setAttribute('data-schema-index', String(index));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="home"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-white">
      <Hero onContactClick={() => setShowContactModal(true)} />
      <AboutFormat />
      <BriefingSteps />
      <CtaSection />
      <PackageFeatures />
      <RecordingLocation />
      <Footer />

      {/* Floating WhatsApp CTA */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 text-white font-bold px-5 py-4 rounded-2xl shadow-2xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
        style={{ background: 'linear-gradient(135deg, #1A3A8F 0%, #2B6FD4 80%, #00BFFF 100%)' }}
        title="Reservar meu episódio"
      >
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-whatsapp-line text-2xl"></i>
        </div>
        <span className="text-sm">Quero reservar meu episódio!</span>
      </a>

      {showContactModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full relative">
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Entre em Contato</h3>
            <p className="text-gray-600 mb-6">
              Para agendar seu episódio, entre em contato através dos nossos canais:
            </p>
            <div className="space-y-4">
              <a
                href="https://instagram.com/manausdigital.br"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl hover:shadow-md transition-all cursor-pointer"
              >
                <i className="ri-instagram-line text-2xl" style={{ color: '#2B6FD4' }}></i>
                <span className="font-medium text-gray-900">@manausdigital.br</span>
              </a>
              <a
                href="mailto:contato@manausdigital.com"
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:shadow-md transition-all cursor-pointer"
              >
                <i className="ri-mail-line text-2xl text-gray-700"></i>
                <span className="font-medium text-gray-900">contato@manausdigital.com</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
